import { sectionRenderer } from '@theme/section-renderer';

const INIT_KEY = '__walletButtonsLayoutBound';

if (!window[INIT_KEY]) {
  window[INIT_KEY] = true;
  bootstrap();
}

function bootstrap() {
  const ORDER = [
    'button-ShopPay',
    'button-AmazonPay',
    'button-ApplePay',
    'button-PayPal',
    'button-GooglePay',
  ];

  const GAP = 8;
  const mql = window.matchMedia('(max-width: 768px)');

  let drawerWalletButtonsWerePresent = !!document.querySelector('cart-drawer-component [data-wallet-buttons]');
  let cartPageWalletButtonsWerePresent = !!getCartPageWalletContainer();

  function getCartPageWalletContainer() {
    return Array.from(document.querySelectorAll('.cart-page [data-wallet-buttons]')).find(
      (container) => !container.closest('cart-drawer-component'),
    );
  }

  function getWalletContainers() {
    const containers = Array.from(document.querySelectorAll('cart-drawer-component [data-wallet-buttons]'));

    if (mql.matches) {
      const cartPageContainer = getCartPageWalletContainer();
      if (cartPageContainer) {
        containers.push(cartPageContainer);
      }
    }

    return containers;
  }

  function getButtons(container) {
    return ORDER
      .map((slot) => container.querySelector('[slot="' + slot + '"]'))
      .filter(Boolean);
  }

  function getHiddenButton(container) {
    if (!mql.matches) return null;
    const all = getButtons(container);
    return all.length === ORDER.length ? all[all.length - 1] : null;
  }

  function reset(container) {
    getButtons(container).forEach((button) => {
      button.style.width = '';
      button.style.left = '';
      button.style.top = '';
      button.style.display = '';
      button.style.removeProperty('min-width');
    });
  }

  function layout(container) {
    const hidden = getHiddenButton(container);
    const present = getButtons(container).filter((button) => button !== hidden);
    const count = present.length;
    if (!count) return;

    const host = container.querySelector('shopify-accelerated-checkout-cart');
    if (!host) return;

    container.classList.remove('wallets-row');
    reset(container);

    if (hidden) {
      hidden.style.display = 'none';
    }

    if (host.getBoundingClientRect().height <= 52) return;

    container.classList.add('wallets-row');

    const hostWidth = host.getBoundingClientRect().width;
    const totalGap = (count - 1) * GAP;
    const colWidthPx = (hostWidth - totalGap) / count;

    present.forEach((button, index) => {
      const isLast = index === count - 1;
      const left = isLast ? hostWidth - colWidthPx : index * (colWidthPx + GAP);
      button.style.width = colWidthPx + 'px';
      button.style.setProperty('min-width', '0', 'important');
      button.style.left = left + 'px';
      button.style.top = '';
    });

    const hostTop = host.getBoundingClientRect().top;
    present.forEach((button) => {
      const offset = button.getBoundingClientRect().top - hostTop;
      if (offset !== 0) {
        button.style.top = -offset + 'px';
      }
    });
  }

  function apply(container) {
    if (mql.matches && getButtons(container).length >= 3) {
      layout(container);
    } else {
      container.classList.remove('wallets-row');
      reset(container);
    }
  }

  function isLaidOut(container) {
    if (!(mql.matches && getButtons(container).length >= 3)) {
      return !container.classList.contains('wallets-row');
    }

    const host = container.querySelector('shopify-accelerated-checkout-cart');
    if (!host) return false;

    const hostRect = host.getBoundingClientRect();
    if (hostRect.height <= 52) return false;
    if (!container.classList.contains('wallets-row')) return false;

    const hidden = getHiddenButton(container);
    const present = getButtons(container).filter((button) => button !== hidden);
    const count = present.length;
    if (!count) return false;

    const hostWidth = hostRect.width;
    const hostTop = hostRect.top;
    const colWidthPx = (hostWidth - (count - 1) * GAP) / count;
    const TOLERANCE = 2;

    const allGood = present.every((button) => {
      if (!button.style.width) return false;
      const rect = button.getBoundingClientRect();
      if (Math.abs(rect.width - colWidthPx) > TOLERANCE) return false;
      if (Math.abs(rect.top - hostTop) > TOLERANCE) return false;
      return true;
    });
    if (!allGood) return false;

    if (hidden && hidden.style.display !== 'none') return false;

    return true;
  }

  const activeTimers = new WeakMap();
  const refreshInProgress = new WeakSet();

  function applyWhenReady(container) {
    if (activeTimers.has(container)) return;

    let attempts = 0;
    let properTicks = 0;
    let sawHost = false;
    const maxAttempts = 40;
    const noHostLimit = 14;

    const tick = () => {
      const host = container.querySelector('shopify-accelerated-checkout-cart');

      if (!host) {
        if (sawHost) return true;
        return attempts >= noHostLimit;
      }
      sawHost = true;

      apply(container);

      if (isLaidOut(container)) {
        properTicks += 1;
      } else {
        properTicks = 0;
      }

      return properTicks >= 2;
    };

    if (tick()) return;

    const timer = setInterval(() => {
      attempts += 1;
      if (tick() || attempts >= maxAttempts) {
        clearInterval(timer);
        activeTimers.delete(container);
      }
    }, 150);

    activeTimers.set(container, timer);
  }

  function applyAll() {
    getWalletContainers().forEach(applyWhenReady);
  }

  let deferredRun = 0;
  function applyAllSoon() {
    applyAll();
    clearTimeout(deferredRun);
    deferredRun = setTimeout(applyAll, 300);
  }

  function getSectionIdForContainer(container) {
    const cartItems = container.closest('cart-items-component');
    return cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
  }

  function getWalletContainerFromSectionHtml(container, fragment) {
    if (container.closest('cart-drawer-component')) {
      return fragment.querySelector('cart-drawer-component [data-wallet-buttons]');
    }

    if (container.closest('.cart-page')) {
      return fragment.querySelector('.cart-page [data-wallet-buttons]');
    }

    return fragment.querySelector('[data-wallet-buttons]');
  }

  async function refreshWalletButtons(container) {
    if (!container || refreshInProgress.has(container)) return false;

    const sectionId = getSectionIdForContainer(container);
    if (!sectionId) return false;

    refreshInProgress.add(container);

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false);
      const fragment = new DOMParser().parseFromString(html, 'text/html');
      const newContainer = getWalletContainerFromSectionHtml(container, fragment);

      if (!newContainer) return false;

      container.innerHTML = newContainer.innerHTML;
      applyWhenReady(container);
      return true;
    } catch (error) {
      console.error(error);
      applyWhenReady(container);
      return false;
    } finally {
      refreshInProgress.delete(container);
    }
  }

  function processDrawerCartUpdate() {
    const isEmpty = !!document.querySelector('cart-drawer-component .cart-drawer--empty');
    const currentContainer = document.querySelector('cart-drawer-component [data-wallet-buttons]');
    const nowPresent = !!currentContainer;

    if (isEmpty) {
      drawerWalletButtonsWerePresent = false;
      return false;
    }

    if (!drawerWalletButtonsWerePresent && nowPresent) {
      drawerWalletButtonsWerePresent = true;
      refreshWalletButtons(currentContainer);
      return true;
    }

    drawerWalletButtonsWerePresent = nowPresent;
    return false;
  }

  function processCartPageCartUpdate() {
    if (!mql.matches) return false;

    const isEmpty = !!document.querySelector('.cart-page--empty');
    const currentContainer = getCartPageWalletContainer();
    const nowPresent = !!currentContainer;

    if (isEmpty) {
      cartPageWalletButtonsWerePresent = false;
      return false;
    }

    if (!cartPageWalletButtonsWerePresent && nowPresent) {
      cartPageWalletButtonsWerePresent = true;
      refreshWalletButtons(currentContainer);
      return true;
    }

    cartPageWalletButtonsWerePresent = nowPresent;
    return false;
  }

  function handleCartUpdate(event) {
    if (event.detail?.data?.didError) return;

    setTimeout(() => {
      const didRefresh = processDrawerCartUpdate() || processCartPageCartUpdate();

      if (!didRefresh) {
        applyAllSoon();
      }
    }, 0);
  }

  function cartPageNeedsRefresh(container) {
    const host = container.querySelector('shopify-accelerated-checkout-cart');
    const buttons = getButtons(container);
    return !host || buttons.length === 0;
  }

  function initCartPage() {
    const container = getCartPageWalletContainer();
    if (!container) return;

    cartPageWalletButtonsWerePresent = true;

    if (cartPageNeedsRefresh(container)) {
      refreshWalletButtons(container);
      return;
    }

    applyWhenReady(container);
  }

  function init() {
    const cartPageContainer = getCartPageWalletContainer();

    if (cartPageContainer) {
      initCartPage();
      return;
    }

    applyAll();
  }

  document.addEventListener('cart:update', handleCartUpdate);
  document.addEventListener('dialog:open', applyAll, true);

  if (document.readyState === 'complete') {
    init();
  } else {
    window.addEventListener('load', init);
  }

  window.addEventListener('pageshow', (event) => {
    const container = getCartPageWalletContainer();
    if (!container) return;

    if (event.persisted || cartPageNeedsRefresh(container)) {
      initCartPage();
    }
  });

  const onBreakpointChange = () => applyAll();
  if (mql.addEventListener) {
    mql.addEventListener('change', onBreakpointChange);
  } else if (mql.addListener) {
    mql.addListener(onBreakpointChange);
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(applyAll, 150);
  });
}
